// ════════════════════════════════════════════════════════════════════
// src/pages/WhatsApp.js — as conversas dos números da conta
// ════════════════════════════════════════════════════════════════════
// Versão simples do FaLei Inbox, para o franqueado e a equipe dele
// trabalharem por aqui: ver, responder (texto, áudio, imagem, arquivo),
// respostas prontas com "/", passar a conversa e etiquetar.
//
// A cara é a do WhatsApp de propósito: quem passa o dia no WhatsApp não
// pode ter que reaprender onde fica cada coisa.
//
// A lista é ordenada por quem espera resposta há mais tempo — ela é o
// painel de urgência. Depois vem o resto, do mais recente para o mais
// antigo.
//
// Quem decide o que cada um pode ver é o servidor (n8n), não esta tela.
// ════════════════════════════════════════════════════════════════════

import { Fragment, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MessageCircle, Search, Send, Tag, Users, RefreshCw, X, Pencil, Trash2, Plus,
  Paperclip, Mic, Zap, Square, FileText, Lock, PenLine, Bookmark, CheckCheck, Archive, RotateCcw, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  listarConversas, lerMensagens, responder, lerMidia, marcar, enviarMidia,
  salvarEtiqueta, apagarEtiqueta, tirarPessoa,
  lerAtalhos, salvarAtalho, apagarAtalho,
  editarMensagem, editarContato, notaInterna,
  marcarLida, fecharAtendimento, reabrirAtendimento, listarFechadas, lerCarteira, lerIndicadores,
  lerMotivos, salvarMotivo, apagarMotivo,
} from '../services/whatsapp';

// Motivos de fechar atendimento: lista fechada (o servidor só aceita estes).
const MOTIVOS_FECHAMENTO = ['Resolvido', 'Venda fechada', 'Sem retorno', 'Outro'];
// O "motivo do contato" (a outra pergunta ao fechar) é uma lista de cada
// conta, editada por Administrador e Vendas (ModalMotivos).
const PERIODOS = [['hoje', 'Hoje'], ['7d', 'Últimos 7 dias'], ['30d', 'Últimos 30 dias'], ['mes', 'Este mês']];
// Telefone comparado pelos 8 últimos dígitos (DDI e nono dígito variam).
const fim8 = (t) => String(t || '').replace(/\D/g, '').slice(-8);

// Cores do WhatsApp Web
const VERDE = '#00a884';
const VERDE_ESCURO = '#008069';
const FUNDO_CHAT = '#efeae2';
const BALAO_NOSSO = '#d9fdd3';
const BALAO_NOTA = '#fff4c2';   // nota interna: amarelo, para nunca confundir com o que o cliente viu
const EDITAR_MS = 15 * 60 * 1000;   // o WhatsApp só deixa editar até 15 min depois do envio
const TEXTO = '#111b21';
const TEXTO_2 = '#667781';
const BORDA = '#e9edef';
const CINZA_BARRA = '#f0f2f5';
const CORES = ['#6c47ff', '#059669', '#d97706', '#dc2626', '#2563eb', '#db2777', '#0891b2', '#52526e'];
const CORES_AVATAR = ['#00a884', '#6c47ff', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#06b6d4', '#8b5cf6'];

const MAX_ARQUIVO = 8 * 1024 * 1024;   // ~8 MB: em base64 vira ~11 MB, o limite do n8n

// ── Formatação ──────────────────────────────────────────────────────
const fmtTel = (c) => {
  const d = String(c || '').replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  return c || '';
};
const fmtHora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const hoje = new Date();
  if (d.toDateString() === hoje.toDateString()) return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};
const soHora = (iso) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const fmtDia = (iso) => {
  const d = new Date(iso);
  const hoje = new Date();
  if (d.toDateString() === hoje.toDateString()) return 'Hoje';
  const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmtEspera = (iso, agora) => {
  const min = Math.max(0, Math.round((agora - new Date(iso).getTime()) / 60000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};
// Verde até 30 min, amarelo até 2h, vermelho depois — igual ao Inbox.
const corEspera = (iso, agora) => {
  const min = (agora - new Date(iso).getTime()) / 60000;
  if (min <= 30) return '#059669';
  if (min <= 120) return '#d97706';
  return '#dc2626';
};
const ROTULO_TIPO = {
  imagem: '📷 Imagem', video: '🎥 Vídeo', audio: '🎵 Áudio', voz: '🎤 Áudio', documento: '📄 Documento',
  figurinha: 'Figurinha', localizacao: '📍 Localização', contato: '👤 Contato',
  protegida: '🔒 Mensagem protegida', desconhecido: 'Mensagem não suportada',
};
const resumo = (tipo, texto) => texto || ROTULO_TIPO[tipo] || '';
const TEM_ARQUIVO = ['imagem', 'video', 'audio', 'voz', 'documento', 'figurinha'];
const semAcento = (v) => String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
// O servidor recusa número do Inbox interno com este motivo.
const ehInterno = (b) => /interno da FaLei/i.test(b.motivo || '');
const nomeConversa = (c) => c.nome || (c.ehGrupo ? 'Grupo' : fmtTel(c.contato));
const corAvatar = (k) => CORES_AVATAR[[...String(k)].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7) % CORES_AVATAR.length];
const inicial = (c) => (c.nome || '').trim().charAt(0).toUpperCase() || (c.ehGrupo ? '👥' : '#');

// *texto* vira negrito, como no WhatsApp (a assinatura usa isso).
const comNegrito = (t) => String(t).split(/(\*[^*\n]+\*)/g).map((p, i) =>
  /^\*[^*\n]+\*$/.test(p) ? <b key={i}>{p.slice(1, -1)}</b> : p);

// O WhatsApp só toca opus/ogg. Gravar em ogg poupa a conversão na
// Evolution; o resto é rede de segurança para navegador que não grava ogg.
function formatoDeGravacao() {
  const tenta = ['audio/ogg;codecs=opus', 'audio/ogg', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  for (const f of tenta) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(f)) return f;
  }
  return '';
}
const paraBase64 = (arquivo) => new Promise((ok, falha) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result).replace(/^data:[^,]*,/, ''));
  r.onerror = () => falha(new Error('Não consegui ler o arquivo.'));
  r.readAsDataURL(arquivo);
});

// ── Estilos ─────────────────────────────────────────────────────────
const chip = (ativo, cor = VERDE_ESCURO) => ({
  padding: '5px 12px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
  border: 'none', background: ativo ? cor + '22' : CINZA_BARRA,
  color: ativo ? cor : '#54656f', whiteSpace: 'nowrap', fontFamily: 'inherit', flexShrink: 0,
});
const iconeBtn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  padding: '8px', borderRadius: '50%', border: 'none', background: 'transparent', cursor: 'pointer',
  color: '#54656f', fontFamily: 'inherit' };
const botao = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '9px',
  border: `1px solid ${BORDA}`, background: '#fff', cursor: 'pointer', fontSize: '12.5px', fontWeight: 600,
  color: '#3b4a54', fontFamily: 'inherit' };
const inp = { padding: '8px 11px', borderRadius: '8px', border: `1px solid ${BORDA}`, fontSize: '13.5px',
  outline: 'none', fontFamily: 'inherit', background: '#fff' };
// Fundo do chat: o bege do WhatsApp com pontinhos discretos.
const papelDeParede = {
  backgroundColor: FUNDO_CHAT,
  backgroundImage: 'radial-gradient(rgba(0,0,0,0.035) 1px, transparent 1px)',
  backgroundSize: '18px 18px',
};

export default function WhatsApp() {
  const { user, contaUid } = useAuth();

  const [dados, setDados] = useState(null);       // resposta de "conversas"
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [agora, setAgora] = useState(Date.now());

  // filtros
  const [quem, setQuem] = useState('todas');       // todas | minhas | semdono | uid
  const [numero, setNumero] = useState('');
  const [etiquetaFiltro, setEtiquetaFiltro] = useState(null);
  const [busca, setBusca] = useState('');
  const [verGrupos, setVerGrupos] = useState(false);
  const [semResposta, setSemResposta] = useState(false);
  const [situacao, setSituacao] = useState('abertas');   // abertas | naoLidas | responder | fechadas
  const [fechadas, setFechadas] = useState(null);         // lista das fechadas (só quando a aba abre)
  const [carteira, setCarteira] = useState([]);           // clientes da aba Clientes (Firebase)

  // conversa aberta
  const [aberta, setAberta] = useState(null);      // { instancia, contato }
  const [modal, setModal] = useState(null);        // 'etiquetas' | 'equipe' | 'atalhos' | 'indicadores'

  // respostas prontas: carregadas uma vez e guardadas aqui
  const [atalhos, setAtalhos] = useState([]);
  const recarregarAtalhos = useCallback(async () => {
    try { setAtalhos(await lerAtalhos()); } catch (e) { /* sem atalhos a caixa funciona igual */ }
  }, []);

  const primeiraVez = useRef(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      // Guardado ANTES de setDados: a função passada ao setDados roda
      // depois, e lendo primeiraVez.current lá dentro ela já veria
      // "false" — o aviso "Gravação ligada agora" sumia sem aparecer.
      const primeira = primeiraVez.current;
      const r = await listarConversas({ conferirGravacao: primeira, incluirSemResposta: semResposta });
      // A gravação só é conferida na abertura; nas atualizações seguintes
      // o aviso que já apareceu continua na tela.
      setDados((d) => ({ ...r, gravacao: primeira ? r.gravacao : (d?.gravacao || []) }));
      primeiraVez.current = false;
      setErro('');
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
      setAgora(Date.now());
    }
  }, [semResposta]);

  // Atualiza sozinho a cada 20 s, menos quando a aba está escondida.
  useEffect(() => {
    if (!user) return;
    carregar();
    const t = setInterval(() => { if (!document.hidden) carregar(); }, 20000);
    return () => clearInterval(t);
  }, [user, carregar]);

  useEffect(() => { if (user) recarregarAtalhos(); }, [user, recarregarAtalhos]);

  // Carteira da aba Clientes: uma leitura do Firebase quando a tela abre.
  // Sem ela a tela funciona igual, só não mostra "Cliente: Fulano".
  useEffect(() => {
    if (!user || !contaUid) return;
    let vivo = true;
    Promise.resolve(lerCarteira(contaUid)).then((l) => { if (vivo) setCarteira(Array.isArray(l) ? l : []); })
      .catch(() => { if (vivo) setCarteira([]); });
    return () => { vivo = false; };
  }, [user, contaUid]);

  // Fechadas: só buscadas quando alguém abre a aba (não entram na recarga).
  const carregarFechadas = useCallback(async () => {
    try { setFechadas(await listarFechadas()); setErro(''); } catch (e) { setErro(e.message); }
  }, []);
  useEffect(() => { if (situacao === 'fechadas') carregarFechadas(); }, [situacao, carregarFechadas]);
  const aoMudarTudo = useCallback(async () => {
    await carregar();
    if (situacao === 'fechadas') await carregarFechadas();
  }, [carregar, carregarFechadas, situacao]);

  const conversas = useMemo(() => dados?.conversas || [], [dados]);
  const eu = dados?.eu;
  const pessoas = useMemo(() => dados?.pessoas || [], [dados]);
  const etiquetas = useMemo(() => dados?.etiquetas || [], [dados]);
  const etqPorId = useMemo(() => Object.fromEntries(etiquetas.map((e) => [e.id, e])), [etiquetas]);

  const filtradas = useMemo(() => {
    const b = semAcento(busca.trim());
    const base = situacao === 'fechadas' ? (fechadas || []) : conversas;
    const lista = base.filter((c) => {
      if (situacao === 'naoLidas' && !c.naoLida) return false;
      if (situacao === 'responder' && !c.paraResponder) return false;
      if (!verGrupos && c.ehGrupo) return false;
      if (numero && c.instancia !== numero) return false;
      if (quem === 'minhas' && c.donoUid !== eu?.uid) return false;
      if (quem === 'semdono' && c.donoUid) return false;
      if (!['todas', 'minhas', 'semdono'].includes(quem) && c.donoUid !== quem) return false;
      if (etiquetaFiltro && !c.etiquetas.includes(etiquetaFiltro)) return false;
      if (b && !semAcento(`${c.nome || ''} ${c.contato} ${c.ultimaTexto || ''}`).includes(b)) return false;
      return true;
    });
    // Quem espera há mais tempo primeiro; depois o mais recente.
    return lista.sort((a, z) => {
      if (a.aguardandoDesde && z.aguardandoDesde) return new Date(a.aguardandoDesde) - new Date(z.aguardandoDesde);
      if (a.aguardandoDesde) return -1;
      if (z.aguardandoDesde) return 1;
      return new Date(z.ultimaEm) - new Date(a.ultimaEm);
    });
  }, [conversas, fechadas, situacao, busca, verGrupos, numero, quem, eu, etiquetaFiltro]);

  const contar = (fn) => conversas.filter((c) => (verGrupos || !c.ehGrupo) && fn(c)).length;
  const conversaAberta = aberta && [...conversas, ...(fechadas || [])]
    .find((c) => c.instancia === aberta.instancia && c.contato === aberta.contato);
  const carteiraPorTel = useMemo(() => {
    const m = {};
    for (const cl of carteira) if (cl.telefone && fim8(cl.telefone).length === 8) m[fim8(cl.telefone)] = cl;
    return m;
  }, [carteira]);
  const carteiraPorId = useMemo(() => Object.fromEntries(carteira.map((cl) => [cl.id, cl])), [carteira]);
  // Cliente da conversa: o vínculo manual vale mais; senão, o telefone.
  const clienteDe = (c) => (c.clienteId
    ? { id: c.clienteId, nome: c.clienteNome, codigo: carteiraPorId[c.clienteId]?.codigo || '', manual: true }
    : carteiraPorTel[fim8(c.contato)] || null);
  const variosNumeros = (dados?.numeros || []).length > 1;

  if (!user) {
    return (
      <div style={{ background: '#fff', borderRadius: '14px', maxWidth: '460px', margin: '60px auto', padding: '32px', textAlign: 'center' }}>
        <MessageCircle size={32} color={VERDE} />
        <h2 style={{ fontSize: '18px', fontWeight: 800, margin: '12px 0 8px' }}>WhatsApp</h2>
        <p style={{ color: TEXTO_2, fontSize: '13px' }}>Entre na sua conta para ver as conversas dos seus números.</p>
      </div>
    );
  }

  const avisos = [
    ...(erro ? [{ cor: '#dc2626', texto: erro }] : []),
    ...(dados?.gravacao || []).filter((g) => g.estado !== 'ok')
      .map((g) => ({ cor: g.estado === 'ligada' ? '#059669' : '#d97706', texto: <><b>{g.instancia}</b>: {g.mensagem}</> })),
    ...(dados?.bloqueados || []).filter((b) => !ehInterno(b)).map((b) => ({ cor: '#dc2626', texto: <><b>{b.instancia}</b>: {b.motivo}</> })),
  ];
  // Números do Inbox interno da FaLei: uma linha só.
  const internos = (dados?.bloqueados || []).filter(ehInterno);
  if (internos.length) {
    avisos.push({ cor: '#54656f', texto: <>
      {internos.length === 1 ? 'Este número é' : `Estes ${internos.length} números são`} do Inbox interno da FaLei e não aparecem aqui:{' '}
      <span style={{ fontFamily: 'monospace' }}>{internos.map((b) => b.instancia).join(', ')}</span>
    </> });
  }
  if (dados && !dados.numeros.length) {
    avisos.push({ cor: '#d97706', texto: <>Nenhum número conectado nesta conta. Conecte um aparelho em <b>Configurações</b> e volte aqui.</> });
  }

  return (
    // Ocupa a tela inteira abaixo do menu de cima. Os avisos têm altura
    // máxima e rolam sozinhos: nunca espremem a conversa até sumir.
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 76px)', minHeight: '520px', gap: '8px',
      fontFamily: '"Segoe UI", Helvetica, Arial, sans-serif', color: TEXTO }}>

      {avisos.length > 0 && (
        <div style={{ maxHeight: '110px', overflowY: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {avisos.map((a, i) => <Faixa key={i} cor={a.cor}>{a.texto}</Faixa>)}
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0, background: '#fff', borderRadius: '10px',
        overflow: 'hidden', boxShadow: '0 1px 3px rgba(11,20,26,0.12)' }}>

        {/* ── Lista ─────────────────────────────────────────── */}
        <div style={{ width: '400px', minWidth: '320px', flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderRight: `1px solid ${BORDA}`, minHeight: 0 }}>

          <div style={{ height: '59px', background: CINZA_BARRA, display: 'flex', alignItems: 'center', padding: '0 12px 0 16px', gap: '4px', flexShrink: 0 }}>
            <span style={{ fontSize: '19px', fontWeight: 700, flex: 1 }}>Conversas</span>
            <button style={iconeBtn} title="Indicadores do atendimento" aria-label="Indicadores" onClick={() => setModal('indicadores')}><BarChart3 size={19} /></button>
            <button style={iconeBtn} title="Respostas prontas" onClick={() => setModal('atalhos')}><Zap size={19} /></button>
            <button style={iconeBtn} title="Etiquetas" aria-label="Etiquetas" onClick={() => setModal('etiquetas')}><Tag size={19} /></button>
            {eu?.admin && (
              <button style={iconeBtn} title="Equipe" aria-label="Equipe" onClick={() => setModal('equipe')}><Users size={19} /></button>
            )}
            <button style={{ ...iconeBtn, opacity: carregando ? 0.4 : 1 }} title="Atualizar" onClick={carregar} disabled={carregando}>
              <RefreshCw size={19} />
            </button>
          </div>

          <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '8px', borderBottom: `1px solid ${BORDA}`, flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={15} style={{ position: 'absolute', left: 12, top: 10, color: '#54656f' }} />
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar nome, telefone ou texto"
                style={{ ...inp, width: '100%', paddingLeft: '38px', boxSizing: 'border-box', background: CINZA_BARRA, border: 'none' }} />
            </div>

            {/* Por pessoa: um clique em cada nome mostra as conversas dela */}
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
              <button style={chip(quem === 'todas')} onClick={() => setQuem('todas')}>Todas {contar(() => true)}</button>
              <button style={chip(quem === 'minhas')} onClick={() => setQuem('minhas')}>Minhas {contar((c) => c.donoUid === eu?.uid)}</button>
              <button style={chip(quem === 'semdono', '#d97706')} onClick={() => setQuem('semdono')}>Sem dono {contar((c) => !c.donoUid)}</button>
              {pessoas.filter((p) => p.uid !== eu?.uid).map((p) => (
                <button key={p.uid} style={chip(quem === p.uid)} onClick={() => setQuem(p.uid)}>
                  {p.nome} {contar((c) => c.donoUid === p.uid)}
                </button>
              ))}
            </div>

            {/* Situação do atendimento */}
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
              <button style={chip(situacao === 'naoLidas', VERDE_ESCURO)} onClick={() => setSituacao(situacao === 'naoLidas' ? 'abertas' : 'naoLidas')}>
                Não lidas {contar((c) => c.naoLida)}
              </button>
              <button style={chip(situacao === 'responder', '#2563eb')} onClick={() => setSituacao(situacao === 'responder' ? 'abertas' : 'responder')}>
                🔖 Para responder {contar((c) => c.paraResponder)}
              </button>
              <button style={chip(situacao === 'fechadas', '#52526e')} onClick={() => setSituacao(situacao === 'fechadas' ? 'abertas' : 'fechadas')}>
                <Archive size={12} style={{ verticalAlign: '-2px' }} /> Fechadas
              </button>
            </div>

            {etiquetas.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
                {etiquetas.map((e) => (
                  <button key={e.id} style={chip(etiquetaFiltro === e.id, e.cor)}
                    onClick={() => setEtiquetaFiltro(etiquetaFiltro === e.id ? null : e.id)}>
                    {e.nome}
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', color: '#54656f' }}>
              {variosNumeros && (
                <select value={numero} onChange={(e) => setNumero(e.target.value)} style={{ ...inp, padding: '4px 8px', fontSize: '12px' }}>
                  <option value="">Todos os números</option>
                  {dados.numeros.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              )}
              <label style={{ display: 'flex', gap: '4px', alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={verGrupos} onChange={(e) => setVerGrupos(e.target.checked)} /> Grupos
              </label>
              <label style={{ display: 'flex', gap: '4px', alignItems: 'center', cursor: 'pointer' }}
                title="Conversas em que só vocês falaram (ex.: disparo que ninguém respondeu)">
                <input type="checkbox" checked={semResposta} onChange={(e) => setSemResposta(e.target.checked)} /> Disparos sem resposta
              </label>
            </div>
          </div>

          <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {!dados && !erro && <Vazio>Carregando conversas…</Vazio>}
            {situacao === 'fechadas' && fechadas === null && <Vazio>Carregando atendimentos fechados…</Vazio>}
            {dados && filtradas.length === 0 && (situacao !== 'fechadas' || fechadas !== null) && (
              <Vazio>{situacao === 'fechadas' ? 'Nenhum atendimento fechado.' : 'Nenhuma conversa aqui.'}</Vazio>
            )}
            {filtradas.map((c) => {
              const sel = aberta && aberta.instancia === c.instancia && aberta.contato === c.contato;
              return (
                <div key={c.instancia + '|' + c.contato} onClick={() => setAberta({ instancia: c.instancia, contato: c.contato })}
                  style={{ display: 'flex', gap: '13px', padding: '0 0 0 13px', cursor: 'pointer', alignItems: 'center',
                    background: sel ? '#f0f2f5' : '#fff' }}>
                  <Avatar c={c} tamanho={49} />
                  <div style={{ flex: 1, minWidth: 0, borderBottom: `1px solid ${BORDA}`, padding: '11px 14px 11px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '16px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontWeight: c.naoLida ? 700 : c.aguardandoDesde ? 600 : 400 }}>
                        {nomeConversa(c)}
                      </span>
                      <span style={{ fontSize: '12px', color: c.naoLida || c.aguardandoDesde ? VERDE : TEXTO_2, fontWeight: c.naoLida || c.aguardandoDesde ? 600 : 400 }}>
                        {fmtHora(c.ultimaEm)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                      <span style={{ fontSize: '14px', color: TEXTO_2, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.ultimaDirecao === 'enviada' && <span style={{ color: '#53bdeb' }}>✓✓ </span>}
                        {resumo(c.ultimaTipo, c.ultimaTexto)}
                      </span>
                      {c.naoLida && (
                        <button title="Marcar como lida" aria-label="Marcar como lida"
                          onClick={async (ev) => { ev.stopPropagation(); try { await marcarLida(c.instancia, c.contato); await carregar(); } catch (e) { setErro(e.message); } }}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: VERDE, display: 'flex' }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: VERDE, display: 'inline-block' }} />
                        </button>
                      )}
                      {c.aguardandoDesde && (
                        <span title="Esperando resposta" style={{ fontSize: '11px', fontWeight: 700, color: '#fff',
                          background: corEspera(c.aguardandoDesde, agora), borderRadius: '999px', padding: '2px 7px', flexShrink: 0 }}>
                          {fmtEspera(c.aguardandoDesde, agora)}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '5px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {c.paraResponder && (
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb', background: '#dbeafe', borderRadius: '999px', padding: '1px 8px' }}>
                          🔖 responder
                        </span>
                      )}
                      {c.fechadaEm && (
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#52526e', background: CINZA_BARRA, borderRadius: '999px', padding: '1px 8px' }}>
                          fechado · {c.fechadaMotivo}{c.motivoContato && <> · {c.motivoContato}</>}
                        </span>
                      )}
                      <span style={{ fontSize: '11px', fontWeight: 600, color: c.donoNome ? '#3b4a54' : '#d97706',
                        background: c.donoNome ? CINZA_BARRA : '#fef3c7', borderRadius: '999px', padding: '1px 8px' }}>
                        {c.donoNome || 'sem dono'}
                      </span>
                      {c.etiquetas.map((id) => etqPorId[id] && (
                        <span key={id} style={{ fontSize: '11px', fontWeight: 600, color: etqPorId[id].cor,
                          background: etqPorId[id].cor + '1c', borderRadius: '999px', padding: '1px 8px' }}>
                          {etqPorId[id].nome}
                        </span>
                      ))}
                      {variosNumeros && <span style={{ fontSize: '10.5px', color: TEXTO_2, fontFamily: 'monospace' }}>{c.instancia}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Conversa ──────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          {conversaAberta ? (
            <Conversa key={conversaAberta.instancia + '|' + conversaAberta.contato}
              c={conversaAberta} eu={eu} pessoas={pessoas} etiquetas={etiquetas}
              atalhos={atalhos} abrirAtalhos={() => setModal('atalhos')}
              cliente={clienteDe(conversaAberta)} carteira={carteira}
              aoMudar={aoMudarTudo} />
          ) : (
            <div style={{ flex: 1, display: 'grid', placeItems: 'center', background: '#f8f9fa', borderBottom: `6px solid ${VERDE}`, textAlign: 'center' }}>
              <div>
                <MessageCircle size={56} color="#c4cdd2" />
                <div style={{ fontSize: '28px', fontWeight: 300, color: '#41525d', marginTop: '12px' }}>WhatsApp do FaLei</div>
                <div style={{ fontSize: '14px', color: TEXTO_2, marginTop: '8px' }}>Escolha uma conversa na lista.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {modal === 'etiquetas' && <ModalEtiquetas etiquetas={etiquetas} podeApagar={!!eu?.admin} fechar={() => setModal(null)} aoMudar={carregar} />}
      {modal === 'equipe' && <ModalEquipe pessoas={pessoas} eu={eu} fechar={() => setModal(null)} aoMudar={carregar} />}
      {modal === 'atalhos' && <ModalAtalhos atalhos={atalhos} fechar={() => setModal(null)} aoMudar={setAtalhos} />}
      {modal === 'indicadores' && <ModalIndicadores pessoas={pessoas} eu={eu} fechar={() => setModal(null)} />}
    </div>
  );
}

function Avatar({ c, tamanho }) {
  return (
    <div style={{ width: tamanho, height: tamanho, borderRadius: '50%', background: corAvatar(c.contato),
      color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: tamanho * 0.4, flexShrink: 0 }}>
      {inicial(c)}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Uma conversa aberta
// ════════════════════════════════════════════════════════════════════
function Conversa({ c, eu, pessoas, etiquetas, atalhos, abrirAtalhos, cliente, carteira, aoMudar }) {
  const [mensagens, setMensagens] = useState(null);
  const [editandoContato, setEditandoContato] = useState(false);
  const [fechando, setFechando] = useState(false);

  // Abrir a conversa marca como lida (para a equipe toda), uma vez.
  const jaMarcou = useRef(false);
  useEffect(() => {
    if (!c.naoLida || jaMarcou.current) return;
    jaMarcou.current = true;
    Promise.resolve(marcarLida(c.instancia, c.contato)).then(() => aoMudar()).catch(() => { /* a lista tenta de novo */ });
  }, [c.naoLida, c.instancia, c.contato, aoMudar]);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [verEtiquetas, setVerEtiquetas] = useState(false);
  const fimRef = useRef(null);
  const qtdRef = useRef(0);

  const carregar = useCallback(async () => {
    try {
      const r = await lerMensagens(c.instancia, c.contato);
      setMensagens(r.mensagens);
      setErro('');
    } catch (e) {
      setErro(e.message);
    }
  }, [c.instancia, c.contato]);

  useEffect(() => {
    carregar();
    const t = setInterval(() => { if (!document.hidden) carregar(); }, 10000);
    return () => clearInterval(t);
  }, [carregar]);

  // Desce até o fim só quando chega mensagem nova, não a cada atualização.
  useEffect(() => {
    const n = mensagens?.length || 0;
    if (n !== qtdRef.current) fimRef.current?.scrollIntoView({ block: 'end' });
    qtdRef.current = n;
  }, [mensagens]);

  const mudar = async (mudancas) => {
    setSalvando(true); setErro('');
    try {
      await marcar(c.instancia, c.contato, mudancas);
      await aoMudar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternarEtiqueta = (id) => {
    const atual = c.etiquetas || [];
    mudar({ etiquetas: atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id] });
  };

  const aposEnviar = async () => { await carregar(); aoMudar(); };

  // Separador de dia ("Hoje", "Ontem", data), como no WhatsApp.
  let diaAnterior = '';

  return (
    <>
      {/* Cabeçalho da conversa */}
      <div style={{ background: CINZA_BARRA, padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '13px',
        flexShrink: 0, minHeight: '59px', boxSizing: 'border-box', flexWrap: 'wrap', borderLeft: `1px solid ${BORDA}` }}>
        <Avatar c={c} tamanho={40} />
        <div style={{ flex: 1, minWidth: '160px' }}>
          <div style={{ fontSize: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {nomeConversa(c)}
            <button style={{ ...iconeBtn, padding: '4px' }} title="Editar dados do contato" aria-label="Editar contato"
              onClick={() => setEditandoContato(true)}><Pencil size={14} /></button>
          </div>
          <div style={{ fontSize: '13px', color: TEXTO_2 }}>
            {c.ehGrupo ? 'Grupo' : fmtTel(c.contato)} · pelo número <span style={{ fontFamily: 'monospace' }}>{c.instancia}</span>
            {c.nomeWhatsapp && c.nomeWhatsapp !== c.nome && <> · no WhatsApp: {c.nomeWhatsapp}</>}
          </div>
          {cliente && (
            <div style={{ fontSize: '12.5px', color: '#3b4a54', marginTop: '2px' }}>
              👤 Cliente: {cliente.codigo && <b>ID {cliente.codigo} · </b>}<b>{cliente.nome}</b>{!cliente.manual && <span style={{ color: TEXTO_2 }}> (pelo telefone)</span>}
              {' · '}<Link to="/clientes" style={{ color: VERDE_ESCURO }}>abrir carteira</Link>
            </div>
          )}
          {c.observacao && (
            <div style={{ fontSize: '12.5px', color: '#3b4a54', marginTop: '2px', whiteSpace: 'pre-wrap' }}>📝 {c.observacao}</div>
          )}
        </div>

        <button style={{ ...botao, background: c.paraResponder ? '#dbeafe' : 'transparent', color: c.paraResponder ? '#2563eb' : botao.color }}
          disabled={salvando} onClick={() => mudar({ paraResponder: !c.paraResponder })}
          title={c.paraResponder ? 'Tirar a marca' : 'Marcar para responder'}>
          <Bookmark size={14} /> {c.paraResponder ? 'Marcada' : 'Para responder'}
        </button>
        {c.fechadaEm ? (
          <button style={botao} disabled={salvando} onClick={async () => {
            setSalvando(true); setErro('');
            try { await reabrirAtendimento(c.instancia, c.contato); await aoMudar(); } catch (e) { setErro(e.message); } finally { setSalvando(false); }
          }}><RotateCcw size={14} /> Reabrir</button>
        ) : (
          <button style={botao} onClick={() => setFechando(true)}><CheckCheck size={14} /> Fechar atendimento</button>
        )}

        <label style={{ fontSize: '12.5px', color: '#54656f', display: 'flex', alignItems: 'center', gap: '6px' }}>
          Dono:
          <select value={c.donoUid || ''} disabled={salvando}
            onChange={(e) => mudar({ donoUid: e.target.value || null })}
            style={{ ...inp, padding: '5px 8px', fontSize: '12.5px' }}>
            <option value="">Sem dono</option>
            {/* dono que saiu da lista continua aparecendo, para não sumir calado */}
            {c.donoUid && !pessoas.some((p) => p.uid === c.donoUid) && <option value={c.donoUid}>{c.donoNome}</option>}
            {pessoas.map((p) => <option key={p.uid} value={p.uid}>{p.uid === eu?.uid ? `${p.nome} (eu)` : p.nome}</option>)}
          </select>
        </label>

        <button style={{ ...botao, background: verEtiquetas ? '#fff' : 'transparent' }} onClick={() => setVerEtiquetas((v) => !v)}>
          <Tag size={14} /> Etiquetas
        </button>
      </div>

      {c.fechadaEm && (
        <div style={{ padding: '7px 16px', background: '#f3f4f6', borderBottom: `1px solid ${BORDA}`, fontSize: '12.5px', color: '#3b4a54', flexShrink: 0 }}>
          <Archive size={12} style={{ verticalAlign: '-2px' }} /> Atendimento fechado: <b>{c.fechadaMotivo}</b>
          {c.motivoContato && <> · motivo: <b>{c.motivoContato}</b></>}
          {c.fechadaPor && <> · por {c.fechadaPor}</>} · {fmtDia(c.fechadaEm)} {soHora(c.fechadaEm)}.
          {' '}Se o cliente escrever de novo, ele volta para a lista sozinho.
        </div>
      )}

      {(c.etiquetas.length > 0 || verEtiquetas) && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', padding: '7px 16px', background: '#f7f8fa',
          borderBottom: `1px solid ${BORDA}`, flexShrink: 0 }}>
          {(verEtiquetas ? etiquetas : etiquetas.filter((e) => c.etiquetas.includes(e.id))).map((e) => (
            <button key={e.id} style={{ ...chip(c.etiquetas.includes(e.id), e.cor), opacity: salvando ? 0.5 : 1,
              cursor: verEtiquetas ? 'pointer' : 'default' }}
              onClick={() => verEtiquetas && !salvando && alternarEtiqueta(e.id)}>
              {e.nome}
            </button>
          ))}
          {verEtiquetas && etiquetas.length === 0 && (
            <span style={{ fontSize: '12px', color: TEXTO_2 }}>
              Nenhuma etiqueta ainda. Crie no ícone de etiqueta, em cima da lista.
            </span>
          )}
        </div>
      )}

      {/* Mensagens */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 7%', ...papelDeParede }}>
        {mensagens === null && !erro && <Vazio>Carregando mensagens…</Vazio>}
        {mensagens && mensagens.length === 0 && <Vazio>Nenhuma mensagem gravada nesta conversa.</Vazio>}
        {(mensagens || []).map((m) => {
          const dia = fmtDia(m.ts);
          const separador = dia !== diaAnterior;
          diaAnterior = dia;
          // Fragmento (e não uma div por mensagem): o separador precisa ser
          // irmão direto das mensagens para conseguir grudar no topo ao rolar.
          return (
            <Fragment key={m.id}>
              {separador && (
                // Grudado no topo ao rolar, como no WhatsApp: sempre dá para
                // ver de que dia são as mensagens na tela.
                <div style={{ textAlign: 'center', margin: '10px 0', position: 'sticky', top: 0, zIndex: 2 }}>
                  <span style={{ background: '#fff', padding: '5px 12px', borderRadius: '8px', fontSize: '12.5px',
                    color: '#54656f', boxShadow: '0 1px 0.5px rgba(11,20,26,0.13)' }}>{dia}</span>
                </div>
              )}
              <Bolha m={m} c={c} aoEditar={carregar} />
            </Fragment>
          );
        })}
        <div ref={fimRef} />
      </div>

      {erro && <div style={{ padding: '6px 16px', background: CINZA_BARRA }}><Faixa cor="#dc2626">{erro}</Faixa></div>}

      <CaixaResposta c={c} eu={eu} atalhos={atalhos} abrirAtalhos={abrirAtalhos} aposEnviar={aposEnviar} />
      {editandoContato && <ModalContato c={c} carteira={carteira} cliente={cliente} fechar={() => setEditandoContato(false)} aoMudar={aoMudar} />}
      {fechando && <ModalFechar c={c} fechar={() => setFechando(false)} aoMudar={aoMudar} />}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// A caixa de resposta: texto, "/" respostas prontas, 📎 arquivo, 🎤 áudio
// ════════════════════════════════════════════════════════════════════
// A escolha "assinar" fica no navegador de cada pessoa. Navegador que
// não deixa guardar (aba anônima) só não lembra — nada quebra.
const lerPreferencia = (k) => { try { return localStorage.getItem(k) === '1'; } catch (e) { return false; } };
const gravarPreferencia = (k, v) => { try { localStorage.setItem(k, v ? '1' : '0'); } catch (e) { /* sem memória, segue */ } };

function CaixaResposta({ c, eu, atalhos, abrirAtalhos, aposEnviar }) {
  const [texto, setTexto] = useState('');
  const [modoNota, setModoNota] = useState(false);
  const [assinar, setAssinar] = useState(() => lerPreferencia('fl_wpp_assinar'));
  const [anexo, setAnexo] = useState(null);       // { arquivo, voz, url, mime, nome }
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [gravando, setGravando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const campo = useRef(null);
  const escolher = useRef(null);
  const gravador = useRef(null);
  const pedacos = useRef([]);
  const relogio = useRef(null);

  // A lista de "/" só abre quando a mensagem COMEÇA com a barra e ainda
  // não tem espaço: escrever "10/12" no meio da frase não abre nada.
  const buscaAtalho = useMemo(() => {
    const m = /^\/([a-zA-Z0-9-]*)$/.exec(texto);
    return m ? m[1].toLowerCase() : null;
  }, [texto]);
  const sugestoes = useMemo(() => {
    if (buscaAtalho === null) return [];
    return atalhos.filter((a) => a.atalho.includes(buscaAtalho)
      || semAcento(a.titulo || '').includes(buscaAtalho)).slice(0, 8);
  }, [atalhos, buscaAtalho]);

  useEffect(() => () => {
    clearInterval(relogio.current);
    try { gravador.current?.stop(); } catch (e) { /* já parado */ }
  }, []);

  const limparAnexo = () => setAnexo((a) => { if (a?.url) URL.revokeObjectURL(a.url); return null; });

  const aceitar = (arquivo, voz) => {
    if (!arquivo) return;
    if (arquivo.size > MAX_ARQUIVO) {
      setErro(`"${arquivo.name || 'arquivo'}" tem ${(arquivo.size / 1048576).toFixed(1)} MB. O limite por aqui é 8 MB — comprima ou mande pelo celular.`);
      return;
    }
    limparAnexo(); setErro('');
    setAnexo({ arquivo, voz: !!voz, url: URL.createObjectURL(arquivo),
      mime: arquivo.type || 'application/octet-stream', nome: arquivo.name || (voz ? 'audio' : 'arquivo') });
  };

  // Grava, PARA, e só então mostra o enviar: dá para ouvir antes de
  // mandar. Mensagem enviada não tem desfazer.
  const comecarGravacao = async () => {
    setErro('');
    const formato = formatoDeGravacao();
    if (!formato || !navigator.mediaDevices?.getUserMedia) {
      setErro('Este navegador não grava áudio. Use o Chrome, ou mande pelo celular.');
      return;
    }
    try {
      const fluxo = await navigator.mediaDevices.getUserMedia({ audio: true });
      const g = new MediaRecorder(fluxo, { mimeType: formato });
      pedacos.current = [];
      g.ondataavailable = (e) => { if (e.data?.size) pedacos.current.push(e.data); };
      g.onstop = () => {
        fluxo.getTracks().forEach((t) => t.stop());
        const bolha = new Blob(pedacos.current, { type: formato.split(';')[0] });
        if (bolha.size > 0) aceitar(new File([bolha], 'audio', { type: bolha.type }), true);
      };
      g.start();
      gravador.current = g;
      setGravando(true); setSegundos(0);
      relogio.current = setInterval(() => setSegundos((n) => n + 1), 1000);
    } catch (e) {
      setErro('Não consegui usar o microfone. Confira a permissão do navegador para este site.');
    }
  };

  const pararGravacao = (descartar) => {
    clearInterval(relogio.current);
    setGravando(false);
    if (descartar) pedacos.current = [];
    try { gravador.current?.stop(); } catch (e) { /* já parado */ }
  };

  // Assinatura: o nome em negrito na primeira linha, como no WhatsApp Business.
  const assinado = (t) => (assinar && eu?.nome && t ? `*${eu.nome}:*\n${t}` : t);

  const enviar = async () => {
    const limpo = texto.trim();
    if (enviando || (!anexo && !limpo)) return;
    setEnviando(true); setErro('');
    try {
      if (modoNota) {
        await notaInterna(c.instancia, c.contato, limpo);
      } else if (anexo) {
        const base64 = await paraBase64(anexo.arquivo);
        await enviarMidia(c.instancia, c.contato, {
          base64, mime: anexo.mime, nome: anexo.nome, legenda: anexo.voz ? '' : assinado(limpo), comoVoz: anexo.voz,
        });
        limparAnexo();
      } else {
        await responder(c.instancia, c.contato, assinado(limpo));
      }
      setTexto('');
      await aposEnviar();
    } catch (e) {
      // O texto e o anexo continuam: dá para tentar de novo sem refazer.
      setErro(e.message);
    } finally {
      setEnviando(false);
      setTimeout(() => campo.current?.focus(), 0);
    }
  };

  // Resposta pronta: o texto vai para a caixa, para ajustar antes de mandar.
  const usar = (a) => {
    setTexto(a.texto || '');
    setTimeout(() => campo.current?.focus(), 0);
  };

  const temAlgo = modoNota ? !!texto.trim() : (!!anexo || !!texto.trim());

  return (
    <div style={{ background: modoNota ? '#fdf6dc' : CINZA_BARRA, padding: '8px 16px 10px', flexShrink: 0, position: 'relative' }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); aceitar(e.dataTransfer.files?.[0]); }}>

      {erro && <div style={{ marginBottom: '8px' }}><Faixa cor="#dc2626">{erro}</Faixa></div>}

      {/* Lista das respostas prontas, por cima da caixa */}
      {buscaAtalho !== null && (
        <div style={{ position: 'absolute', left: 16, right: 16, bottom: '100%', background: '#fff', borderRadius: '8px',
          boxShadow: '0 -2px 10px rgba(11,20,26,0.15)', maxHeight: '280px', overflowY: 'auto', zIndex: 5 }}>
          {sugestoes.map((a, i) => (
            <div key={a.id} onMouseDown={(e) => { e.preventDefault(); usar(a); }}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: `1px solid ${BORDA}`,
                background: i === 0 ? '#f5f6f6' : '#fff', display: 'flex', gap: '10px', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 700, color: VERDE_ESCURO, fontSize: '13px', flexShrink: 0 }}>/{a.atalho}</span>
              <span style={{ fontSize: '13px', color: TEXTO_2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.titulo ? <b style={{ color: TEXTO }}>{a.titulo} · </b> : null}{a.texto}
              </span>
            </div>
          ))}
          {sugestoes.length === 0 && (
            <div style={{ padding: '10px 14px', fontSize: '13px', color: TEXTO_2 }}>
              Nenhuma resposta pronta com esse nome.{' '}
              <button style={{ border: 'none', background: 'none', color: VERDE_ESCURO, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                onMouseDown={(e) => { e.preventDefault(); abrirAtalhos(); }}>
                Criar ou ver todas
              </button>
            </div>
          )}
        </div>
      )}

      {/* Prévia do que vai junto: mandar imagem errada não tem conserto */}
      {anexo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#fff', borderRadius: '8px',
          padding: '8px 10px', marginBottom: '8px' }}>
          {anexo.mime.startsWith('image/') ? <img src={anexo.url} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 6 }} />
            : anexo.mime.startsWith('video/') ? <video src={anexo.url} style={{ width: 80, height: 54, borderRadius: 6 }} />
            : anexo.voz || anexo.mime.startsWith('audio/') ? <audio src={anexo.url} controls style={{ height: 36 }} />
            : <FileText size={30} color={TEXTO_2} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {anexo.voz ? 'Áudio gravado — ouça antes de enviar' : anexo.nome}
            </div>
            <div style={{ fontSize: '11.5px', color: TEXTO_2 }}>
              {(anexo.arquivo.size / 1024).toFixed(0)} KB{!anexo.voz && ' · o que você escrever vai como legenda'}
            </div>
          </div>
          <button style={iconeBtn} title="Tirar" onClick={limparAnexo}><X size={18} /></button>
        </div>
      )}

      {/* Nota interna e assinatura */}
      <div style={{ display: 'flex', gap: '14px', alignItems: 'center', marginBottom: '6px', fontSize: '12.5px', color: '#54656f' }}>
        <label style={{ display: 'flex', gap: '5px', alignItems: 'center', cursor: 'pointer', fontWeight: modoNota ? 700 : 400,
          color: modoNota ? '#8a6100' : undefined }}>
          <input type="checkbox" checked={modoNota} onChange={(e) => { setModoNota(e.target.checked); limparAnexo(); }} />
          <Lock size={13} /> Nota interna (o cliente não vê)
        </label>
        {!modoNota && (
          <label style={{ display: 'flex', gap: '5px', alignItems: 'center', cursor: 'pointer' }}
            title={eu?.nome ? `A mensagem sai com *${eu.nome}:* na primeira linha` : ''}>
            <input type="checkbox" checked={assinar}
              onChange={(e) => { setAssinar(e.target.checked); gravarPreferencia('fl_wpp_assinar', e.target.checked); }} />
            <PenLine size={13} /> Assinar com meu nome
          </label>
        )}
      </div>

      {gravando ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', borderRadius: '8px', padding: '6px 8px 6px 14px' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#dc2626' }} />
          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {String(Math.floor(segundos / 60)).padStart(2, '0')}:{String(segundos % 60).padStart(2, '0')}
          </span>
          <span style={{ flex: 1, fontSize: '13px', color: TEXTO_2 }}>gravando… você ouve antes de enviar</span>
          <button style={{ ...iconeBtn, color: '#dc2626' }} title="Descartar" onClick={() => pararGravacao(true)}><Trash2 size={19} /></button>
          <button style={{ ...iconeBtn, background: VERDE, color: '#fff' }} title="Parar" onClick={() => pararGravacao(false)}><Square size={16} /></button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
          <button style={iconeBtn} title="Respostas prontas (ou digite / na caixa)" onClick={abrirAtalhos}><Zap size={22} /></button>
          <button style={iconeBtn} title="Anexar imagem, vídeo ou arquivo" onClick={() => escolher.current?.click()} disabled={enviando || modoNota}>
            <Paperclip size={22} />
          </button>
          <input ref={escolher} type="file" style={{ display: 'none' }} accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(e) => { aceitar(e.target.files?.[0]); e.target.value = ''; }} />
          <textarea ref={campo} value={texto} rows={1}
            onChange={(e) => setTexto(e.target.value)}
            onPaste={(e) => {
              // Colar print direto na caixa (nota interna é só texto)
              if (modoNota) return;
              const item = [...(e.clipboardData?.items || [])].find((i) => i.kind === 'file');
              if (item) { e.preventDefault(); aceitar(item.getAsFile()); }
            }}
            onKeyDown={(e) => {
              // Com a lista do "/" aberta, Enter escolhe a primeira em vez de
              // mandar "/preco" cru para o cliente.
              if (e.key === 'Enter' && !e.shiftKey && sugestoes.length) { e.preventDefault(); usar(sugestoes[0]); return; }
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
            }}
            placeholder={modoNota ? 'Nota para a equipe — o cliente não recebe' : anexo ? 'Legenda (opcional)' : 'Digite uma mensagem ou / para respostas prontas'}
            style={{ flex: 1, border: modoNota ? '1px solid #f0d77a' : 'none', borderRadius: '8px', padding: '10px 14px', fontSize: '15px', outline: 'none',
              fontFamily: 'inherit', resize: 'none', minHeight: '42px', maxHeight: '140px', lineHeight: 1.4, boxSizing: 'border-box',
              background: modoNota ? BALAO_NOTA : '#fff' }} />
          {temAlgo ? (
            <button onClick={enviar} disabled={enviando} title={modoNota ? 'Salvar nota interna' : 'Enviar'} aria-label="Enviar"
              style={{ ...iconeBtn, background: modoNota ? '#c99a06' : VERDE, color: '#fff', padding: '10px', opacity: enviando ? 0.5 : 1 }}>
              <Send size={20} />
            </button>
          ) : (
            <button style={iconeBtn} title="Gravar áudio" onClick={comecarGravacao} disabled={enviando || modoNota}><Mic size={22} /></button>
          )}
        </div>
      )}
      {enviando && <div style={{ fontSize: '11.5px', color: TEXTO_2, marginTop: '4px', textAlign: 'right' }}>Enviando…</div>}
    </div>
  );
}

// ── Uma mensagem ────────────────────────────────────────────────────
function Bolha({ m, c, aoEditar }) {
  const { instancia, ehGrupo } = c;
  const nota = m.direcao === 'interna';
  const nossa = m.direcao === 'enviada' || nota;
  const [midia, setMidia] = useState(null);   // { url, mime }
  const [estado, setEstado] = useState('');   // '' | 'carregando' | mensagem de erro
  const [editando, setEditando] = useState(null);   // texto em edição
  const [erroEdicao, setErroEdicao] = useState('');
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  // Regra do WhatsApp: só texto que nós mandamos, até 15 min depois.
  const podeEditar = m.direcao === 'enviada' && m.tipo === 'texto' && Date.now() - new Date(m.ts).getTime() < EDITAR_MS;

  const salvarEdicao = async () => {
    const t = (editando || '').trim();
    if (!t) { setErroEdicao('O texto não pode ficar vazio.'); return; }
    setSalvandoEdicao(true); setErroEdicao('');
    try {
      await editarMensagem(instancia, c.contato, m.id, t);
      setEditando(null);
      await aoEditar();
    } catch (e) {
      setErroEdicao(e.message);
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const abrirMidia = async () => {
    setEstado('carregando');
    try {
      const r = await lerMidia(instancia, m.id);
      const mime = (r.mime || '').split(';')[0] || 'application/octet-stream';
      const b64 = String(r.base64).replace(/^data:[^,]*,/, '');
      setMidia({ url: `data:${mime};base64,${b64}`, mime });
      setEstado('');
    } catch (e) {
      setEstado(e.message);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: nossa ? 'flex-end' : 'flex-start', marginBottom: '3px' }}>
      <div style={{ maxWidth: 'min(65%, 560px)', background: nota ? BALAO_NOTA : nossa ? BALAO_NOSSO : '#fff', borderRadius: '7.5px',
        padding: '6px 7px 8px 9px', boxShadow: '0 1px 0.5px rgba(11,20,26,0.13)', fontSize: '14.2px', lineHeight: '19px',
        wordBreak: 'break-word', border: nota ? '1px dashed #e0c25a' : 'none' }}>
        {nota && (
          <div style={{ fontSize: '11.5px', fontWeight: 700, color: '#8a6100', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Lock size={11} /> Nota interna · o cliente não vê
          </div>
        )}
        {ehGrupo && !nossa && m.nome && <div style={{ fontSize: '12.8px', fontWeight: 600, color: corAvatar(m.nome), marginBottom: '2px' }}>{m.nome}</div>}

        {TEM_ARQUIVO.includes(m.tipo) && (
          <div style={{ marginBottom: m.texto ? '4px' : 0 }}>
            {midia ? (
              midia.mime.startsWith('image/') ? <img src={midia.url} alt="" style={{ maxWidth: '300px', maxHeight: '320px', borderRadius: '6px', display: 'block' }} />
              : midia.mime.startsWith('audio/') ? <audio src={midia.url} controls autoPlay style={{ width: '260px' }} />
              : midia.mime.startsWith('video/') ? <video src={midia.url} controls style={{ maxWidth: '300px', borderRadius: '6px' }} />
              : <a href={midia.url} download={m.texto || 'arquivo'} style={{ color: VERDE_ESCURO, fontWeight: 600 }}>Baixar arquivo</a>
            ) : (
              <button onClick={abrirMidia} disabled={estado === 'carregando'}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '6px', border: 'none',
                  background: 'rgba(11,20,26,0.05)', cursor: 'pointer', fontSize: '13.5px', color: '#3b4a54', fontFamily: 'inherit' }}>
                {estado === 'carregando' ? 'Carregando…' : `${ROTULO_TIPO[m.tipo] || 'Arquivo'} · abrir`}
              </button>
            )}
            {estado && estado !== 'carregando' && <div style={{ color: '#dc2626', fontSize: '11.5px', marginTop: '4px' }}>{estado}</div>}
          </div>
        )}

        {editando !== null ? (
          <div style={{ minWidth: '240px' }}>
            <textarea autoFocus value={editando} rows={3} onChange={(e) => setEditando(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); salvarEdicao(); } if (e.key === 'Escape') setEditando(null); }}
              style={{ ...inp, width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
              <button style={{ ...botao, background: VERDE, color: '#fff', border: 'none', padding: '4px 10px' }}
                disabled={salvandoEdicao} onClick={salvarEdicao}>{salvandoEdicao ? 'Salvando…' : 'Salvar'}</button>
              <button style={{ ...botao, padding: '4px 10px' }} onClick={() => { setEditando(null); setErroEdicao(''); }}>Cancelar</button>
            </div>
            {erroEdicao && <div style={{ color: '#dc2626', fontSize: '11.5px', marginTop: '4px' }}>{erroEdicao}</div>}
          </div>
        ) : m.texto && m.tipo !== 'documento' && <span style={{ whiteSpace: 'pre-wrap' }}>{comNegrito(m.texto)}</span>}
        {m.tipo === 'documento' && m.texto && <div style={{ fontSize: '13px', color: '#3b4a54' }}>{m.texto}</div>}
        {!m.texto && !TEM_ARQUIVO.includes(m.tipo) && <span style={{ color: TEXTO_2, fontStyle: 'italic' }}>{ROTULO_TIPO[m.tipo] || m.tipo}</span>}

        <span style={{ float: 'right', fontSize: '11px', color: TEXTO_2, margin: '6px 0 -4px 10px', whiteSpace: 'nowrap' }}>
          {podeEditar && editando === null && (
            <button title="Editar (até 15 min depois do envio)" aria-label="Editar mensagem" onClick={() => setEditando(m.texto || '')}
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 4px 0 0', color: TEXTO_2, verticalAlign: 'middle' }}>
              <Pencil size={11} />
            </button>
          )}
          {m.editadaEm && <span style={{ fontStyle: 'italic' }}>editada · </span>}
          {nossa && m.enviadoPor && <span style={{ fontWeight: 600, color: nota ? '#8a6100' : VERDE_ESCURO }}>{m.enviadoPor} · </span>}
          {soHora(m.ts)}
          {m.direcao === 'enviada' && <span style={{ color: '#53bdeb', marginLeft: '3px' }}>✓✓</span>}
        </span>
        <div style={{ clear: 'both' }} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Fechar atendimento: escolhe o motivo e a conversa sai da lista
// ════════════════════════════════════════════════════════════════════
function ModalFechar({ c, fechar, aoMudar }) {
  const [motivo, setMotivo] = useState('');
  const [motivoContato, setMotivoContato] = useState('');
  const [lista, setLista] = useState(null);          // { motivos, podeEditar }
  const [editandoLista, setEditandoLista] = useState(false);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    lerMotivos().then((r) => { if (vivo) setLista(r); }).catch((e) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, []);
  const nomesMotivos = (lista?.motivos || []).map((m) => m.nome);
  // "Sem retorno": o cliente sumiu, então o motivo do contato é opcional.
  const motivoOpcional = motivo === 'Sem retorno';

  const confirmar = async () => {
    setOcupado(true); setErro('');
    try {
      await fecharAtendimento(c.instancia, c.contato, motivo, motivoContato);
      await aoMudar();
      fechar();
    } catch (e) { setErro(e.message); } finally { setOcupado(false); }
  };

  const opcoes = (titulo, nome, lista, valor, mudar) => (
    <fieldset style={{ border: 'none', padding: 0, margin: '0 0 14px' }}>
      <legend style={{ fontSize: '12.5px', fontWeight: 700, marginBottom: '6px' }}>{titulo}</legend>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px 12px' }}>
        {lista.map((m) => (
          <label key={m} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
            <input type="radio" name={nome} checked={valor === m} onChange={() => mudar(m)} /> {m}
          </label>
        ))}
      </div>
    </fieldset>
  );

  return (
    <Modal titulo="Fechar atendimento" fechar={fechar} largura="480px">
      <p style={{ fontSize: '12.5px', color: TEXTO_2, marginTop: 0 }}>
        A conversa com <b>{nomeConversa(c)}</b> sai da lista e vai para "Fechadas".
        Se o cliente escrever de novo, ela volta sozinha. Fechou errado? Reabra em até 30 min e ele sai dos números.
      </p>
      {!lista && !erro && <div style={{ fontSize: '12.5px', color: TEXTO_2, marginBottom: '12px' }}>Carregando os motivos…</div>}
      {lista && opcoes(motivoOpcional ? 'Motivo do contato (opcional)' : 'Motivo do contato', 'motivoContato', nomesMotivos, motivoContato, setMotivoContato)}
      {lista?.podeEditar && (
        <button style={{ ...botao, marginTop: '-6px', marginBottom: '14px', fontSize: '12px' }} onClick={() => setEditandoLista(true)}>
          <Pencil size={12} /> Editar lista de motivos
        </button>
      )}
      {opcoes('Resultado', 'motivo', MOTIVOS_FECHAMENTO, motivo, setMotivo)}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={{ ...botao, background: VERDE, color: '#fff', border: 'none' }} disabled={ocupado || !motivo || (!motivoContato && !motivoOpcional)} onClick={confirmar}>
          {ocupado ? 'Fechando…' : 'Fechar atendimento'}
        </button>
        <button style={botao} onClick={fechar}>Cancelar</button>
      </div>
      {erro && <div style={{ marginTop: '8px' }}><Faixa cor="#dc2626">{erro}</Faixa></div>}
      {editandoLista && (
        <ModalMotivos fechar={() => setEditandoLista(false)} aoMudar={(motivos) => {
          setLista((l) => ({ ...l, motivos }));
          if (!motivos.some((m) => m.nome === motivoContato)) setMotivoContato('');
        }} />
      )}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// Motivos do contato da conta: criar, renomear, apagar
// ════════════════════════════════════════════════════════════════════
// Administrador e Vendas editam (o servidor confere). Apagar ou renomear
// não muda os atendimentos já fechados: eles guardam o nome que tinham.
function ModalMotivos({ fechar, aoMudar }) {
  const [motivos, setMotivos] = useState(null);
  const [editando, setEditando] = useState(null);   // { id, nomeAntigo, nome } — id/nomeAntigo vazios = novo
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    lerMotivos().then((r) => { if (vivo) setMotivos(r.motivos || []); }).catch((e) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, []);

  const aplicar = (r) => { setMotivos(r.motivos || []); if (aoMudar) aoMudar(r.motivos || []); };

  const salvar = async () => {
    setOcupado(true); setErro('');
    try {
      aplicar(await salvarMotivo(editando));
      setEditando(null);
    } catch (e) { setErro(e.message); } finally { setOcupado(false); }
  };

  const apagar = async (m) => {
    if (!window.confirm(`Apagar o motivo "${m.nome}"?\n\nOs atendimentos já fechados com ele continuam contando nos indicadores.`)) return;
    setOcupado(true); setErro('');
    try { aplicar(await apagarMotivo(m)); } catch (x) { setErro(x.message); } finally { setOcupado(false); }
  };

  return (
    <Modal titulo="Motivos do contato" fechar={fechar}>
      <p style={{ fontSize: '12.5px', color: TEXTO_2, marginTop: 0 }}>
        A lista que aparece ao fechar um atendimento. Administrador e Vendas editam. Apagar ou renomear não muda
        os atendimentos já fechados.
      </p>
      {!motivos && !erro && <div style={{ fontSize: '12.5px', color: TEXTO_2 }}>Carregando…</div>}
      {(motivos || []).map((m) => (
        <div key={m.id || m.nome} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: `1px solid ${BORDA}` }}>
          <span style={{ flex: 1, fontSize: '13px' }}>{m.nome}</span>
          <button style={botao} disabled={ocupado} aria-label={`Renomear ${m.nome}`}
            onClick={() => setEditando({ id: m.id, nomeAntigo: m.nome, nome: m.nome })}><Pencil size={12} /></button>
          <button style={{ ...botao, color: '#dc2626' }} disabled={ocupado} aria-label={`Apagar ${m.nome}`}
            onClick={() => apagar(m)}><Trash2 size={12} /></button>
        </div>
      ))}

      {motivos && (editando ? (
        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input autoFocus value={editando.nome} maxLength={40} placeholder="Nome do motivo (ex.: Emissão boleto)"
            aria-label="Nome do motivo"
            onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && editando.nome.trim() && salvar()} style={inp} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...botao, background: VERDE, color: '#fff', border: 'none' }} disabled={ocupado || !editando.nome.trim()} onClick={salvar}>
              {editando.nomeAntigo ? 'Salvar' : 'Criar'}
            </button>
            <button style={botao} onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      ) : (
        <button style={{ ...botao, marginTop: '14px' }} onClick={() => setEditando({ nome: '' })}><Plus size={13} /> Novo motivo</button>
      ))}
      {erro && <div style={{ marginTop: '8px' }}><Faixa cor="#dc2626">{erro}</Faixa></div>}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// Indicadores do atendimento: fechados, em aberto, motivos, por pessoa
// ════════════════════════════════════════════════════════════════════
// Os números vêm prontos do servidor (as contas são feitas no banco):
// abrir esta janela é uma chamada só, sem baixar conversa nenhuma.
function ModalIndicadores({ pessoas, eu, fechar }) {
  const [periodo, setPeriodo] = useState('7d');
  const [atendente, setAtendente] = useState('');
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    let vivo = true;
    setCarregando(true); setErro('');
    lerIndicadores(periodo, atendente || null)
      .then((r) => { if (vivo) setDados(r); })
      .catch((e) => { if (vivo) setErro(e.message); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [periodo, atendente]);

  const [editandoMotivos, setEditandoMotivos] = useState(false);
  const podeEditarMotivos = !!eu && (eu.admin || eu.papel === 'vendas');
  const ind = dados?.indicadores;
  const nomeDe = (uid) => (pessoas || []).find((p) => p.uid === uid)?.nome || uid;

  return (
    <Modal titulo="Indicadores do atendimento" fechar={fechar} largura="680px">
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} aria-label="Período" style={inp}>
          {PERIODOS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
        </select>
        <select value={atendente} onChange={(e) => setAtendente(e.target.value)} aria-label="Atendente" style={inp}>
          <option value="">Geral (todos)</option>
          {eu?.uid && <option value={eu.uid}>Só eu</option>}
          {(pessoas || []).filter((p) => p.uid !== eu?.uid).map((p) => <option key={p.uid} value={p.uid}>{p.nome}</option>)}
        </select>
        {carregando && <span style={{ fontSize: '12.5px', color: TEXTO_2, alignSelf: 'center' }}>Carregando…</span>}
        {podeEditarMotivos && (
          <button style={{ ...botao, marginLeft: 'auto' }} onClick={() => setEditandoMotivos(true)}><Pencil size={13} /> Motivos</button>
        )}
      </div>
      {editandoMotivos && <ModalMotivos fechar={() => setEditandoMotivos(false)} />}

      {erro && <div style={{ marginBottom: '10px' }}><Faixa cor="#dc2626">{erro}</Faixa></div>}

      {ind && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '18px' }}>
            <Numero rotulo="Fechados" valor={ind.fechados} cor={VERDE_ESCURO} />
            <Numero rotulo="Em aberto" valor={ind.abertos} cor="#2563eb" />
            <Numero rotulo="Esperando resposta" valor={ind.aguardando} cor="#d97706" />
            {!atendente && <Numero rotulo="Em aberto sem dono" valor={ind.abertosSemDono} cor="#dc2626" />}
          </div>
          <div style={{ fontSize: '11.5px', color: TEXTO_2, margin: '-10px 0 16px' }}>
            {dados.periodoNome}. "Em aberto" são as conversas não fechadas que tiveram mensagem no período
            {atendente ? ' (do dono escolhido)' : ''}; "Fechados" conta quem fechou.
          </div>

          <Barras titulo="Principais motivos do contato" itens={ind.porMotivo} cor={VERDE} vazio="Nenhum atendimento fechado no período." />
          <Barras titulo="Resultado" itens={ind.porResultado} cor="#6c47ff" vazio="Nenhum atendimento fechado no período." />

          {!atendente && (
            <div style={{ marginTop: '6px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>Por atendente</div>
              {ind.porAtendente.length === 0 ? (
                <div style={{ fontSize: '12.5px', color: TEXTO_2 }}>Ninguém fechou ou tem conversa em aberto no período.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: TEXTO_2 }}>
                      <th style={{ padding: '4px 0', fontWeight: 600 }}>Pessoa</th>
                      <th style={{ padding: '4px 0', fontWeight: 600, textAlign: 'right' }}>Fechados</th>
                      <th style={{ padding: '4px 0', fontWeight: 600, textAlign: 'right' }}>Em aberto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ind.porAtendente.map((p) => (
                      <tr key={p.uid} style={{ borderTop: `1px solid ${BORDA}`, cursor: 'pointer' }} onClick={() => setAtendente(p.uid)}
                        title="Ver só os números desta pessoa">
                        <td style={{ padding: '6px 0' }}>{p.nome || nomeDe(p.uid)}</td>
                        <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.fechados}</td>
                        <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p.abertos}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function Numero({ rotulo, valor, cor }) {
  return (
    <div style={{ border: `1px solid ${BORDA}`, borderRadius: '10px', padding: '10px 12px' }}>
      <div style={{ fontSize: '12px', color: TEXTO_2 }}>{rotulo}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: cor, fontVariantNumeric: 'tabular-nums' }}>{valor ?? 0}</div>
    </div>
  );
}

function Barras({ titulo, itens, cor, vazio }) {
  const maior = Math.max(1, ...(itens || []).map((i) => i.total));
  const soma = (itens || []).reduce((t, i) => t + i.total, 0);
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>{titulo}</div>
      {!itens || itens.length === 0 ? (
        <div style={{ fontSize: '12.5px', color: TEXTO_2 }}>{vazio}</div>
      ) : itens.map((i) => (
        <div key={i.nome} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 190px) 1fr 70px', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
          <span style={{ fontSize: '12.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.nome}>{i.nome}</span>
          <div style={{ background: CINZA_BARRA, borderRadius: '4px', height: '14px' }}>
            <div style={{ width: `${(i.total / maior) * 100}%`, background: cor, height: '100%', borderRadius: '4px' }} />
          </div>
          <span style={{ fontSize: '12.5px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {i.total} <span style={{ color: TEXTO_2 }}>({Math.round((i.total / soma) * 100)}%)</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Dados do contato: nome que a equipe usa e uma observação
// ════════════════════════════════════════════════════════════════════
function ModalContato({ c, carteira, cliente, fechar, aoMudar }) {
  const [nome, setNome] = useState(c.nome || '');
  const [observacao, setObservacao] = useState(c.observacao || '');
  // '' = automático (pelo telefone); senão o id do cliente escolhido.
  const [clienteId, setClienteId] = useState(c.clienteId || '');
  const [buscaCliente, setBuscaCliente] = useState('');
  const opcoes = useMemo(() => {
    const b = semAcento(buscaCliente.trim());
    const dig = b.replace(/\D/g, '');
    return (carteira || []).filter((cl) => !b || semAcento(cl.nome).includes(b)
      || (cl.codigo && semAcento(cl.codigo).includes(b))
      || (dig && (cl.telefone.includes(dig) || cl.documento.includes(dig)))).slice(0, 50);
  }, [carteira, buscaCliente]);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const salvar = async () => {
    setOcupado(true); setErro('');
    try {
      // Nome igual ao do WhatsApp = não guarda nada (continua acompanhando o WhatsApp).
      const nomeFinal = nome.trim() && nome.trim() !== (c.nomeWhatsapp || '') ? nome.trim() : '';
      const dados = { nome: nomeFinal, observacao };
      // Vínculo com a carteira só vai quando alguém mexeu nele.
      if (clienteId !== (c.clienteId || '')) {
        const escolhido = (carteira || []).find((cl) => cl.id === clienteId);
        dados.clienteId = clienteId || null;
        dados.clienteNome = escolhido ? escolhido.nome : null;
      }
      await editarContato(c.instancia, c.contato, dados);
      await aoMudar();
      fechar();
    } catch (e) { setErro(e.message); } finally { setOcupado(false); }
  };

  return (
    <Modal titulo="Dados do contato" fechar={fechar}>
      <div style={{ fontSize: '12.5px', color: TEXTO_2, marginBottom: '10px' }}>
        {fmtTel(c.contato)}{c.nomeWhatsapp && <> · nome no WhatsApp: <b>{c.nomeWhatsapp}</b></>}
      </div>
      <label style={{ fontSize: '12.5px', fontWeight: 600 }}>Nome</label>
      <input autoFocus value={nome} maxLength={80} placeholder={c.nomeWhatsapp || 'Nome do contato'}
        onChange={(e) => setNome(e.target.value)} style={{ ...inp, width: '100%', boxSizing: 'border-box', margin: '4px 0 10px' }} />
      <label style={{ fontSize: '12.5px', fontWeight: 600 }}>Observação (só a equipe vê)</label>
      <textarea value={observacao} rows={4} maxLength={1000} placeholder="Ex.: dona do salão, prefere falar à tarde"
        onChange={(e) => setObservacao(e.target.value)} style={{ ...inp, width: '100%', boxSizing: 'border-box', margin: '4px 0 6px', resize: 'vertical' }} />
      <div style={{ fontSize: '11.5px', color: TEXTO_2, marginBottom: '10px' }}>
        Deixe o nome em branco para voltar a usar o nome do WhatsApp.
      </div>
      <label style={{ fontSize: '12.5px', fontWeight: 600 }}>Cliente da carteira (aba Clientes)</label>
      {(carteira || []).length === 0 ? (
        <div style={{ fontSize: '12px', color: TEXTO_2, margin: '4px 0 10px' }}>Nenhum cliente na carteira desta conta.</div>
      ) : (
        <>
          <input value={buscaCliente} placeholder="Buscar pelo ID do cliente, nome, CNPJ ou telefone" onChange={(e) => setBuscaCliente(e.target.value)}
            style={{ ...inp, width: '100%', boxSizing: 'border-box', margin: '4px 0 6px' }} />
          <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} aria-label="Cliente da carteira"
            style={{ ...inp, width: '100%', boxSizing: 'border-box', marginBottom: '4px' }}>
            <option value="">{!c.clienteId && cliente ? `Automático pelo telefone: ${cliente.nome}` : 'Automático (pelo telefone)'}</option>
            {c.clienteId && !opcoes.some((cl) => cl.id === c.clienteId) && <option value={c.clienteId}>{c.clienteNome || c.clienteId}</option>}
            {opcoes.map((cl) => <option key={cl.id} value={cl.id}>{cl.codigo ? `ID ${cl.codigo} · ` : ''}{cl.nome}{cl.telefone ? ` · ${fmtTel('55' + cl.telefone.replace(/^55/, ''))}` : ''}</option>)}
          </select>
          <div style={{ fontSize: '11.5px', color: TEXTO_2, marginBottom: '10px' }}>
            No automático, o cliente aparece quando o telefone da conversa bate com o da carteira.
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={{ ...botao, background: VERDE, color: '#fff', border: 'none' }} disabled={ocupado} onClick={salvar}>
          {ocupado ? 'Salvando…' : 'Salvar'}
        </button>
        <button style={botao} onClick={fechar}>Cancelar</button>
      </div>
      {erro && <div style={{ marginTop: '8px' }}><Faixa cor="#dc2626">{erro}</Faixa></div>}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// Respostas prontas da conta: criar, editar, apagar (toda a equipe)
// ════════════════════════════════════════════════════════════════════
function ModalAtalhos({ atalhos, fechar, aoMudar }) {
  const [editando, setEditando] = useState(null);   // { id?, atalho, titulo, texto }
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const salvar = async () => {
    setOcupado(true); setErro('');
    try {
      aoMudar(await salvarAtalho(editando));
      setEditando(null);
    } catch (e) { setErro(e.message); } finally { setOcupado(false); }
  };

  const apagar = async (a) => {
    if (!window.confirm(`Apagar a resposta pronta /${a.atalho}?`)) return;
    setOcupado(true); setErro('');
    try { aoMudar(await apagarAtalho(a.id)); } catch (e) { setErro(e.message); } finally { setOcupado(false); }
  };

  return (
    <Modal titulo="Respostas prontas" fechar={fechar} largura="520px">
      <p style={{ fontSize: '12.5px', color: TEXTO_2, marginTop: 0 }}>
        Na caixa de resposta, digite <b>/</b> e o atalho (ex.: <b>/preco</b>). O texto entra na caixa para você ajustar antes de enviar.
        A lista é da conta inteira: toda a equipe usa e pode editar.
      </p>

      {!editando && (
        <>
          {atalhos.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 0', borderBottom: `1px solid ${BORDA}` }}>
              <span style={{ fontWeight: 700, color: VERDE_ESCURO, fontSize: '13px', minWidth: '90px' }}>/{a.atalho}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {a.titulo && <div style={{ fontSize: '13px', fontWeight: 600 }}>{a.titulo}</div>}
                <div style={{ fontSize: '12.5px', color: TEXTO_2, whiteSpace: 'pre-wrap', maxHeight: '54px', overflow: 'hidden' }}>{a.texto}</div>
              </div>
              <button style={botao} disabled={ocupado} onClick={() => setEditando({ id: a.id, atalho: a.atalho, titulo: a.titulo || '', texto: a.texto })}><Pencil size={12} /></button>
              <button style={{ ...botao, color: '#dc2626' }} disabled={ocupado} onClick={() => apagar(a)}><Trash2 size={12} /></button>
            </div>
          ))}
          {atalhos.length === 0 && <div style={{ fontSize: '12.5px', color: TEXTO_2 }}>Nenhuma resposta pronta ainda.</div>}
          <button style={{ ...botao, marginTop: '14px' }} onClick={() => setEditando({ atalho: '', titulo: '', texto: '' })}>
            <Plus size={13} /> Nova resposta pronta
          </button>
        </>
      )}

      {editando && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: VERDE_ESCURO }}>/</span>
            <input autoFocus value={editando.atalho} maxLength={30} placeholder="atalho (ex.: preco)"
              onChange={(e) => setEditando({ ...editando, atalho: e.target.value })} style={{ ...inp, width: '150px' }} />
            <input value={editando.titulo} maxLength={60} placeholder="Título (opcional)"
              onChange={(e) => setEditando({ ...editando, titulo: e.target.value })} style={{ ...inp, flex: 1 }} />
          </div>
          <textarea value={editando.texto} rows={6} placeholder="A mensagem…"
            onChange={(e) => setEditando({ ...editando, texto: e.target.value })} style={{ ...inp, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...botao, background: VERDE, color: '#fff', border: 'none' }}
              disabled={ocupado || !editando.atalho.trim() || !editando.texto.trim()} onClick={salvar}>
              {editando.id ? 'Salvar' : 'Criar'}
            </button>
            <button style={botao} onClick={() => { setEditando(null); setErro(''); }}>Cancelar</button>
          </div>
        </div>
      )}
      {erro && <Faixa cor="#dc2626">{erro}</Faixa>}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// Etiquetas da conta (administrador): criar, renomear, trocar cor, apagar
// ════════════════════════════════════════════════════════════════════
function ModalEtiquetas({ etiquetas, podeApagar, fechar, aoMudar }) {
  const [editando, setEditando] = useState(null);   // { id?, nome, cor }
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const salvar = async () => {
    setOcupado(true); setErro('');
    try {
      await salvarEtiqueta(editando);
      setEditando(null);
      await aoMudar();
    } catch (e) { setErro(e.message); } finally { setOcupado(false); }
  };

  const apagar = async (e) => {
    if (!window.confirm(`Apagar a etiqueta "${e.nome}"?\n\nEla sai de todas as conversas que a usam.`)) return;
    setOcupado(true); setErro('');
    try { await apagarEtiqueta(e.id); await aoMudar(); } catch (x) { setErro(x.message); } finally { setOcupado(false); }
  };

  return (
    <Modal titulo="Etiquetas da conta" fechar={fechar}>
      <p style={{ fontSize: '12.5px', color: TEXTO_2, marginTop: 0 }}>
        Toda a equipe cria, edita e aplica. Apagar é só com o administrador da conta. Lista curta funciona melhor que lista grande.
      </p>
      {etiquetas.map((e) => (
        <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: `1px solid ${BORDA}` }}>
          <span style={{ width: 12, height: 12, borderRadius: 4, background: e.cor }} />
          <span style={{ flex: 1, fontSize: '13px' }}>{e.nome}</span>
          <button style={botao} disabled={ocupado} onClick={() => setEditando({ id: e.id, nome: e.nome, cor: e.cor })}><Pencil size={12} /></button>
          {podeApagar && <button style={{ ...botao, color: '#dc2626' }} disabled={ocupado} onClick={() => apagar(e)}><Trash2 size={12} /></button>}
        </div>
      ))}
      {etiquetas.length === 0 && <div style={{ fontSize: '12.5px', color: TEXTO_2 }}>Nenhuma etiqueta ainda.</div>}

      {editando ? (
        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input autoFocus value={editando.nome} maxLength={40} placeholder="Nome (ex.: quente, retornar, fechado)"
            onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && salvar()} style={inp} />
          <div style={{ display: 'flex', gap: '6px' }}>
            {CORES.map((cor) => (
              <span key={cor} onClick={() => setEditando({ ...editando, cor })}
                style={{ width: 22, height: 22, borderRadius: 6, background: cor, cursor: 'pointer',
                  outline: editando.cor === cor ? `2px solid ${cor}` : 'none', outlineOffset: 2 }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...botao, background: VERDE, color: '#fff', border: 'none' }} disabled={ocupado || !editando.nome.trim()} onClick={salvar}>
              {editando.id ? 'Salvar' : 'Criar'}
            </button>
            <button style={botao} onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      ) : (
        <button style={{ ...botao, marginTop: '14px' }} onClick={() => setEditando({ nome: '', cor: CORES[0] })}><Plus size={13} /> Nova etiqueta</button>
      )}
      {erro && <Faixa cor="#dc2626">{erro}</Faixa>}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// Equipe no WhatsApp (administrador)
// ════════════════════════════════════════════════════════════════════
function ModalEquipe({ pessoas, eu, fechar, aoMudar }) {
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const tirar = async (p) => {
    if (!window.confirm(`Tirar ${p.nome} da lista do WhatsApp?\n\nAs conversas dele(a) ficam sem dono para outra pessoa pegar. Se ${p.nome} ainda tiver acesso e abrir a tela, volta para a lista sozinho(a) — para tirar o acesso de vez, use a tela Equipe.`)) return;
    setOcupado(true); setErro('');
    try { await tirarPessoa(p.uid); await aoMudar(); } catch (e) { setErro(e.message); } finally { setOcupado(false); }
  };

  return (
    <Modal titulo="Quem atende no WhatsApp" fechar={fechar}>
      <p style={{ fontSize: '12.5px', color: TEXTO_2, marginTop: 0 }}>
        A pessoa aparece aqui (e no "Dono" das conversas) depois de abrir esta tela pela primeira vez.
        Para convidar alguém, use a tela <b>Equipe</b>.
      </p>
      {pessoas.map((p) => (
        <div key={p.uid} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderBottom: `1px solid ${BORDA}` }}>
          <span style={{ flex: 1, fontSize: '13px' }}>{p.nome}{p.uid === eu?.uid && ' (eu)'}</span>
          <span style={{ fontSize: '11px', color: TEXTO_2 }}>{p.papel}</span>
          {p.uid !== eu?.uid && (
            <button style={{ ...botao, color: '#dc2626' }} disabled={ocupado} onClick={() => tirar(p)}><Trash2 size={12} /></button>
          )}
        </div>
      ))}
      {erro && <Faixa cor="#dc2626">{erro}</Faixa>}
    </Modal>
  );
}

// ── Pedacinhos ──────────────────────────────────────────────────────
function Faixa({ cor, children }) {
  return (
    <div style={{ background: cor + '12', border: `1px solid ${cor}40`, color: cor, borderRadius: '8px',
      padding: '7px 12px', fontSize: '12.5px', lineHeight: 1.5 }}>
      {children}
    </div>
  );
}

function Vazio({ children }) {
  return <div style={{ padding: '40px 20px', textAlign: 'center', color: TEXTO_2, fontSize: '13.5px' }}>{children}</div>;
}

function Modal({ titulo, fechar, children, largura = '420px' }) {
  return (
    <div onClick={fechar} style={{ position: 'fixed', inset: 0, background: 'rgba(11,20,26,0.4)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', width: largura, maxWidth: '92vw',
        maxHeight: '82vh', overflowY: 'auto', padding: '22px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '17px', fontWeight: 700, margin: 0, flex: 1 }}>{titulo}</h3>
          <button onClick={fechar} style={{ border: 'none', background: 'none', cursor: 'pointer', color: TEXTO_2 }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
